import React from 'react';
import { Navigate, Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCalendarCheck, FaMapMarkerAlt, FaPhone, FaRoute } from 'react-icons/fa';
import SEO, { SITE_URL } from './SEO';
import { getLocalPageBySlug, localPages } from './localPages';
import Footer from './Footer';
import AuditForm from './AuditForm';
import './LocalLandingPage.css';

const ORG_ID = `${SITE_URL}/#organization`;

// One specific line per service — replaces the repeated boilerplate that made
// every card on every landing page read identically.
const SERVICE_BLURBS = {
  'AI phone receptionists': 'Answers in under two seconds, in your business name, every hour of the year.',
  'Website AI agents': 'A chat agent on your site that answers questions and books while visitors are still deciding.',
  'Lead qualification': 'Asks your screening questions and hands your team only the callers worth a callback.',
  'Appointment booking': 'Checks real availability, books the slot, and confirms with the caller on the same call.',
  'CRM automation': 'Every call becomes a clean CRM record — contact, intent, outcome — without anyone typing.',
  'Customer follow-up': 'Automatic text and email follow-ups so quotes and no-shows stop going quiet.',
  'Inbound call answering': 'Every inbound call picked up on the first ring — rush hour, lunch, and 2am included.',
  'Reservation and appointment booking': 'Takes reservations and bookings straight into your calendar while you serve the room.',
  'Spanish-friendly call flows': 'Callers are served naturally in English or Spanish — no transfers, no call-backs.',
  'Call summaries': 'A plain-English summary of every call, texted or emailed to you as it ends.',
  'Warm transfers': 'Urgent or high-value callers get transferred live to your staff with context, not cold.',
  'After-hours capture': 'Calls after closing become booked appointments and captured leads, not voicemails.',
  'Front-desk call handling': 'Covers the front-desk phone so your in-person guests get your full attention.',
  'Appointment scheduling': 'Books, reschedules, and confirms directly in your scheduling system.',
  'Customer intake': 'Collects the details you need — name, reason, insurance, urgency — before your team picks up.',
  'Message taking': 'Accurate messages with name, number, and what the caller wanted — never a scribbled note.',
  'Calendar sync': 'Bookings land on your team calendar instantly, so double-booking stops happening.',
  'Staff escalation': 'Clear rules for what gets escalated to a human, and to whom, at any hour.',
  'Reservation calls': 'Party size, time, name, number — captured and confirmed while your host works the door.',
  'Menu and hours questions': 'Hours, parking, menu, and dietary questions answered instantly, every time.',
  'Catering inquiries': 'Catering leads qualified and captured with event date, headcount, and budget.',
  'No-show reminders': 'Automated confirmations and reminders that measurably cut no-shows.',
  'Host stand backup': 'When the host stand is slammed, the AI takes the overflow instead of voicemail.',
  'After-hours messages': 'Late-night callers get answers and next steps, not a full mailbox.',
  'New patient intake': 'New patients are welcomed, screened, and scheduled before staff touches the file.',
  'Recall reminders': 'Automated recall outreach that quietly refills your hygiene and follow-up slots.',
  'Insurance question routing': 'Coverage questions collected up front and routed to the right person once.',
  'Staff handoff': 'Complex calls reach your team with a summary already attached.',
  'New matter intake': 'Potential clients screened with your conflict and intake questions, verbatim.',
  'Urgency routing': 'Time-sensitive matters identified and routed to the on-call person immediately.',
  'Consultation scheduling': 'Qualified prospects land directly on your consultation calendar.',
  'CRM notes': 'Every conversation logged in your CRM with intent, outcome, and next step.',
  'After-hours coverage': 'Nights, weekends, and holidays answered at the same standard as 10am Tuesday.',
  '24/7 inbound call answering': 'Every call answered around the clock — including the ones from a job site at 6am.',
  'Job qualification + intake': 'Job type, location, timeline, and budget captured before you call back.',
  'Estimate scheduling': 'Estimate visits booked into your crew calendar while the lead is still hot.',
  'After-hours emergency triage': 'Real emergencies reach your on-call tech; everything else books for morning.',
  'CRM + ServiceTitan / Jobber sync': 'Jobs, notes, and bookings land in ServiceTitan or Jobber automatically.',
  '24/7 appointment booking': 'Clients book, reschedule, and cancel by phone at any hour — no app required.',
  'Rescheduling and cancellation handling': 'Cancellations become rebooked slots instead of empty chairs.',
  'Service + pricing questions': 'Service menu and price-range questions answered consistently and accurately.',
  'New-client intake': 'First-time clients captured with preferences, history, and how they found you.',
  'Calendar sync (Vagaro, Square, Boulevard, Mindbody, Fresha)': 'Bookings flow straight into Vagaro, Square, Boulevard, Mindbody, or Fresha.',
  'SMS follow-up + reminders': 'Confirmations and reminders by text that keep your book full.',
  'Reservation calls + waitlist': 'Reservations and waitlist management handled while service continues.',
  'Catering and large-party inquiries': 'Big-ticket event calls qualified with date, headcount, and budget.',
  'Menu + hours + directions': 'The questions that interrupt your line, answered without staff.',
  'OpenTable / Resy / Tock sync': 'Phone reservations land in OpenTable, Resy, or Tock like any other booking.',
  'Appointment scheduling + recall reminders': 'Scheduling plus automated recalls that keep the chair filled.',
  'Insurance + new-patient questions': 'Insurance and new-patient questions handled with your exact policies.',
  'Urgency routing to on-call staff': 'True emergencies escalate to on-call staff; routine calls book normally.',
  'Practice management system sync': 'Appointments and notes sync into your practice management system.',
  '24/7 maintenance call intake + triage': 'Maintenance calls triaged by urgency — floods escalate, faucets book.',
  'Leasing inquiry qualification + showing booking': 'Prospects qualified and showings booked before a competitor calls back.',
  'After-hours emergency routing': 'After-hours emergencies reach the right vendor or manager immediately.',
  'Rent, availability & application questions': 'Availability, rent, and application questions answered without your staff.',
  'Owner & vendor call handling': 'Owners and vendors routed correctly with clean notes on every call.',
  'PM software sync (AppFolio, Buildium, Yardi, Rent Manager)': 'Work orders and leads sync to AppFolio, Buildium, Yardi, or Rent Manager.',
};

function buildServiceSchema(page) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: page.title,
    description: page.description,
    provider: {
      '@type': 'LocalBusiness',
      '@id': ORG_ID,
      name: 'UBOTIKA',
      url: SITE_URL,
      telephone: '+1-310-912-3548',
      email: 'hello@ubotika.com',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Los Angeles',
        addressRegion: 'CA',
        addressCountry: 'US',
      },
      sameAs: [
        'https://x.com/ubotika',
        'https://www.youtube.com/@Ubotika',
        'https://www.linkedin.com/company/112106919/',
        'https://maps.google.com/?cid=886761037595198009',
      ],
    },
    areaServed: {
      '@type': 'City',
      name: 'Los Angeles',
    },
    serviceType: page.primaryKeyword,
    offers: {
      '@type': 'Offer',
      availability: 'https://schema.org/InStock',
      priceCurrency: 'USD',
    },
  };
}

function buildFaqSchema(page) {
  if (!page.faqs || !page.faqs.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: page.faqs.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a,
      },
    })),
  };
}

function buildBreadcrumbSchema(page) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'AI Agents', item: `${SITE_URL}/ai-agents-los-angeles` },
      { '@type': 'ListItem', position: 3, name: page.shortTitle, item: `${SITE_URL}/${page.slug}` },
    ],
  };
}

function LocalLandingPage() {
  const { slug } = useParams();
  const page = getLocalPageBySlug(slug);

  if (!page) return <Navigate to="/ai-agents-los-angeles" replace />;

  // Rotate through the list instead of always slicing the first five: the old `.slice(0, 5)`
  // only ever surfaced the LA pages at the top of localPages.js, which left the five industry
  // pages (contractors, salons, restaurants, clinics, property management) with zero inbound
  // internal links. Cycling gives every page exactly five inbound links.
  const pageIndex = localPages.findIndex((item) => item.slug === page.slug);
  const relatedPages = Array.from(
    { length: Math.min(5, localPages.length - 1) },
    (_, offset) => localPages[(pageIndex + 1 + offset) % localPages.length]
  );

  return (
    <>
      <SEO
        title={`${page.primaryKeyword} | UBOTIKA`}
        description={page.description}
        path={`/${page.slug}`}
        image={`${SITE_URL}/og/${page.slug}.png`}
        schema={[
          buildServiceSchema(page),
          buildFaqSchema(page),
          buildBreadcrumbSchema(page),
        ].filter(Boolean)}
      />
      <main id="main-content" className="local-page">
        <section className="local-hero">
          <div className="local-hero-grid">
            <motion.div
              className="local-hero-copy"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <div className="local-eyebrow">
                <FaMapMarkerAlt />
                <span>{page.eyebrow}</span>
              </div>
              <h1>{page.title}</h1>
              <p className="local-lede">{page.description}</p>
              <div className="local-actions">
                <a
                  href="tel:+14244344541"
                  className="cta-button local-primary-cta"
                  onClick={() => {
                    if (typeof window !== 'undefined' && window.gtag) {
                      window.gtag('event', 'demo_call_click', { location: `local-${page.slug}` });
                    }
                    if (typeof window !== 'undefined' && window.fbq) {
                      window.fbq('trackCustom', 'DemoCallClick', { location: `local-${page.slug}` });
                    }
                  }}
                >
                  <FaPhone />
                  Call our AI now — (424) 434-4541
                </a>
                <a href="#audit-form" className="local-secondary-cta">
                  <FaCalendarCheck />
                  Get a Free Audit
                </a>
              </div>
            </motion.div>
            <motion.aside
              className="local-proof-panel"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
            >
              <p>{page.proof}</p>
              <dl>
                <div>
                  <dt>Service area</dt>
                  <dd>Los Angeles County</dd>
                </div>
                <div>
                  <dt>Setup</dt>
                  <dd>Usually a few days</dd>
                </div>
                <div>
                  <dt>Coverage</dt>
                  <dd>Calls, chat, CRM, follow-up</dd>
                </div>
              </dl>
            </motion.aside>
          </div>
        </section>

        <section className="local-audit-cta" id="audit-form">
          <div className="local-section-inner local-audit-grid">
            <div className="local-audit-copy">
              <span className="local-kicker">Free · No call required</span>
              <h2>See exactly which leads you&apos;re losing</h2>
              <p>
                We review your website and phone flow by hand, then send one custom
                AI-agent mockup for {page.audience} — in your inbox within 48 hours.
                No call, no pitch.
              </p>
            </div>
            <div className="audit-form-wrap">
              <AuditForm src={page.slug} />
            </div>
          </div>
        </section>

        {page.answer && (
          <section className="local-answer">
            <div className="local-section-inner">
              <p className="local-answer-text">{page.answer}</p>
            </div>
          </section>
        )}

        <section className="local-band">
          <div className="local-section-inner">
            <div className="local-section-heading">
              <span>What We Build</span>
              <h2>AI agents built around your existing customer flow</h2>
              <p>
                UBOTIKA designs, launches, and tunes AI agents for {page.audience}. The goal is simple:
                answer faster, document better, and keep every good lead moving.
              </p>
            </div>
            <div className="local-feature-grid">
              {page.services.map((service) => (
                <article className="local-feature" key={service}>
                  <FaRoute />
                  <h3>{service}</h3>
                  <p>
                    {SERVICE_BLURBS[service] ||
                      'Configured around your hours, policies, handoff rules, and the tools your team already uses.'}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="local-outcomes">
          <div className="local-section-inner local-outcome-grid">
            <div>
              <span className="local-kicker">Business Outcomes</span>
              <h2>Why Los Angeles teams use AI agents</h2>
              <p>
                Search traffic is only useful when it turns into calls, bookings, and qualified conversations.
                These systems are built to close the gap between a visitor finding you and your team following up.
              </p>
            </div>
            <ul className="local-outcome-list">
              {page.outcomes.map((outcome) => (
                <li key={outcome}>{outcome}</li>
              ))}
            </ul>
          </div>
        </section>

        {page.faqs && page.faqs.length > 0 && (
          <section className="local-faq">
            <div className="local-section-inner">
              <div className="local-section-heading">
                <span>Frequently Asked Questions</span>
                <h2>{`${page.shortTitle} — common questions`}</h2>
              </div>
              <div className="local-faq-list">
                {page.faqs.map((item) => (
                  <article className="local-faq-item" key={item.q}>
                    <h3>{item.q}</h3>
                    <p>{item.a}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="local-related">
          <div className="local-section-inner">
            <div className="local-section-heading compact">
              <span>Related LA Services</span>
              <h2>Explore more AI agent pages</h2>
            </div>
            <div className="local-related-links">
              {relatedPages.map((item) => (
                <Link key={item.slug} to={`/${item.slug}`}>
                  {item.shortTitle}
                </Link>
              ))}
            </div>
          </div>
        </section>
        <Footer />
      </main>
    </>
  );
}

export default LocalLandingPage;
